import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createProduct, getProductPricing } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';

const CreateProduct = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [bulkSpecMode, setBulkSpecMode] = useState(false);
  const [bulkSpecText, setBulkSpecText] = useState('');
  const [brandOptions, setBrandOptions] = useState([]);
  // When true, the brand dropdown is set to "Other" and a manual text input is shown.
  const [isOtherBrand, setIsOtherBrand] = useState(false);
  const [productData, setProductData] = useState({
    category_id: 1,
    name: '',
    description: '',
    thumbnail_image: null,
    original_image: null,
    images: [],
    data_sheet: null,
    price: '',
    discount_price: [{ max: '', qty: 1, price: '' }],
    warranty: '',
    specifications: [{ key: '', value: '' }],
    brand: '',
    wattage: '',
    sku_code: '',
    short_name: '',
    tags: '',
    dcr_applicable: false,
    hsn_code: ''
  });

  useEffect(() => {
    // Check if user has superAdmin role
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role !== 'superAdmin' && !userData.access?.includes("marketing")) {
      // Redirect to orders page if not superAdmin
      window.location.href = '/orders';
      return;
    }

    // Pre-fill form if copying from an existing product
    if (location.state?.copyFrom) {
      setProductData(prev => ({
        ...prev,
        ...location.state.copyFrom,
        // Always reset images/files — they can't be copied as file references
        thumbnail_image: null,
        original_image: null,
        images: [],
        data_sheet: null,
      }));
    }

    // Load existing brands to populate the brand dropdown.
    loadBrands(location.state?.copyFrom?.brand);
  }, []);

  // Fetch existing products and derive a unique, sorted list of brands for the
  // dropdown. If a pre-filled brand (e.g. from "copy product") isn't in that
  // list, switch the field to "Other" so it stays editable as free text.
  const loadBrands = async (prefilledBrand) => {
    try {
      const response = await getProductPricing(1);
      const list = response?.data || [];
      const brands = [...new Set(
        list.map(p => (p.brand || '').trim()).filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      setBrandOptions(brands);

      if (prefilledBrand && !brands.includes(prefilledBrand.trim())) {
        setIsOtherBrand(true);
      }
    } catch (error) {
      console.error('Error loading brands:', error);
      // On failure, fall back to manual entry so the user is never blocked.
      setIsOtherBrand(true);
    }
  };

  // Handle brand dropdown selection. Choosing "__other__" reveals a manual
  // text input and clears any previously selected brand value.
  const handleBrandSelectChange = (e) => {
    const value = e.target.value;
    if (value === '__other__') {
      setIsOtherBrand(true);
      setProductData(prev => ({ ...prev, brand: '' }));
    } else {
      setIsOtherBrand(false);
      setProductData(prev => ({ ...prev, brand: value }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductData({
      ...productData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      // Validate file type - allow PDF for data_sheet, images for others
      if (fieldName === 'data_sheet') {
        if (file.type !== 'application/pdf') {
          toast.error('Only PDF files are allowed for data sheet');
          return;
        }
      } else {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
          toast.error('Only JPEG and PNG images are allowed');
          return;
        }
      }

      setProductData({
        ...productData,
        [fieldName]: file
      });
    }
  };

  const handleMultipleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate each file
    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Each file size must be less than 5MB');
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPEG and PNG images are allowed');
        return;
      }
    }

    setProductData({
      ...productData,
      images: files
    });
  };

  const handleDiscountPriceChange = (index, field, value) => {
    const updatedPrices = [...productData.discount_price];
    updatedPrices[index] = {
      ...updatedPrices[index],
      [field]: field === 'price' || field === 'max' || field === 'qty' ? parseFloat(value) || '' : value
    };
    setProductData({
      ...productData,
      discount_price: updatedPrices
    });
  };

  const addDiscountPriceSlab = () => {
    setProductData({
      ...productData,
      discount_price: [...productData.discount_price, { max: '', qty: '', price: '' }]
    });
  };

  const removeDiscountPriceSlab = (index) => {
    if (productData.discount_price.length > 1) {
      const updatedPrices = productData.discount_price.filter((_, i) => i !== index);
      setProductData({
        ...productData,
        discount_price: updatedPrices
      });
    }
  };

  const handleSpecificationChange = (index, field, value) => {
    const updatedSpecs = [...productData.specifications];
    updatedSpecs[index] = {
      ...updatedSpecs[index],
      [field]: value
    };
    setProductData({
      ...productData,
      specifications: updatedSpecs
    });
  };

  const addSpecification = () => {
    setProductData({
      ...productData,
      specifications: [...productData.specifications, { key: '', value: '' }]
    });
  };

  const removeSpecification = (index) => {
    if (productData.specifications.length > 1) {
      const updatedSpecs = productData.specifications.filter((_, i) => i !== index);
      setProductData({
        ...productData,
        specifications: updatedSpecs
      });
    }
  };

  const parseBulkSpecText = (text) => {
    const newSpecs = [];
    (text || '').split('\n').filter(line => line.trim()).forEach(line => {
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        const key = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();
        if (key && value) {
          newSpecs.push({ key, value });
        }
      }
    });
    return newSpecs;
  };

  const toggleBulkSpecMode = () => {
    if (!bulkSpecMode) {
      // Convert current specifications to bulk text format
      const bulkText = productData.specifications
        .filter(spec => spec.key && spec.value)
        .map(spec => `${spec.key}:${spec.value}`)
        .join('\n');
      setBulkSpecText(bulkText);
    } else {
      // Convert bulk text back to specifications array
      const newSpecs = parseBulkSpecText(bulkSpecText);

      // Ensure at least one empty specification if none were parsed
      if (newSpecs.length === 0) {
        newSpecs.push({ key: '', value: '' });
      }
      
      setProductData({
        ...productData,
        specifications: newSpecs
      });
    }
    setBulkSpecMode(!bulkSpecMode);
  };

  const handleBulkSpecTextChange = (e) => {
    setBulkSpecText(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!productData.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    if (!productData.brand.trim()) {
      toast.error('Brand is required');
      return;
    }

    if (!productData.sku_code.trim()) {
      toast.error('SKU Code is required');
      return;
    }

    if (!productData.hsn_code.trim()) {
      toast.error('HSN Code is required');
      return;
    }
    
    if (productData.category_id === 1 && (!productData.wattage || productData.wattage <= 0)) {
      toast.error('Wattage is mandatory for Solar Panels');
      return;
    }

    setLoading(true);
    try {
      // Prepare form data for file upload
      const formData = new FormData();
      
      // Add basic fields
      formData.append('category_id', productData.category_id);
      formData.append('name', productData.name);
      formData.append('description', productData.description);
      formData.append('price', productData.price);
      formData.append('warranty', productData.warranty);
      formData.append('brand', productData.brand);
      formData.append('wattage', productData.wattage);
      formData.append('sku_code', productData.sku_code);
      formData.append('short_name', productData.short_name || '');
      formData.append('tags', productData.tags || '');
      formData.append('dcr_applicable', productData.dcr_applicable || false);
      formData.append('hsn_code', productData.hsn_code || '');
      
      // Add JSON fields
      formData.append('discount_price', JSON.stringify(productData.discount_price));

      const specsSource = bulkSpecMode
        ? parseBulkSpecText(bulkSpecText)
        : productData.specifications;
      const specsObject = {};
      specsSource.forEach(spec => {
        if (spec.key && spec.value) {
          specsObject[spec.key] = spec.value;
        }
      });
      formData.append('specifications', JSON.stringify(specsObject));
      
      // Add files
      if (productData.thumbnail_image) {
        formData.append('thumbnail_image', productData.thumbnail_image);
      }
      if (productData.original_image) {
        formData.append('original_image', productData.original_image);
      }
      
      // Add multiple images
      productData.images.forEach((image, index) => {
        formData.append('images', image);
      });

      // Add data sheet (PDF) if provided
      if (productData.data_sheet instanceof File) {
        formData.append('data_sheet', productData.data_sheet);
      }

      const response = await createProduct(formData);

      if (response && response.success === false) {
        const parts = [];
        if (response.message) parts.push(response.message);
        if (Array.isArray(response.errors) && response.errors.length) {
          const fieldMsgs = response.errors
            .map((e) => (e.field ? `${e.field}: ${e.message}` : e.message))
            .filter(Boolean);
          if (fieldMsgs.length) parts.push(fieldMsgs.join(', '));
        }
        toast.error(parts.join(' — ') || 'Failed to create product');
        return;
      }

      const createdProduct = response?.data || response?.product;
      if (createdProduct && createdProduct.id) {
        toast.success('Product created successfully!');
        navigate('/products');
      } else {
        toast.error(response?.message || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      // Show the real reason (validation details / which field is wrong)
      // that was surfaced by the API layer, not a generic message.
      toast.error(error?.message || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/products">Products</Link> / Create New Product
            </div>
            <h1>Create New Product</h1>
          </div>
          
          <div className="order-actions">
            <Link to="/products" className="btn btn-secondary">
              Cancel
            </Link>
            <button
              type="submit"
              form="create-product-form"
              disabled={loading}
              className="btn btn-success"
            >
              {loading ? 'Creating...' : 'Create Product'}
            </button>
          </div>
        </div>

        <form id="create-product-form" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="card">
            <h3 className="section-title">Basic Information</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Category *</label>
                <select
                  name="category_id"
                  className="form-control"
                  value={productData.category_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value={1}>Solar Panels</option>
                  <option value={2}>Inverters</option>
                  <option value={3}>BOS</option>
                  <option value={4}>Kit</option>
                  <option value={5}>Battery</option>
                </select>
              </div>
              
              <div className="info-item">
                <label className="info-label">Product Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={productData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter product name"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Brand *</label>
                <select
                  className="form-control"
                  value={isOtherBrand ? '__other__' : productData.brand}
                  onChange={handleBrandSelectChange}
                  required={!isOtherBrand}
                >
                  <option value="" disabled>Select a brand</option>
                  {brandOptions.map((brand) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                  <option value="__other__">Other (enter manually)</option>
                </select>
                {isOtherBrand && (
                  <input
                    type="text"
                    name="brand"
                    className="form-control"
                    value={productData.brand}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter brand name"
                    style={{ marginTop: '8px' }}
                    autoFocus
                  />
                )}
              </div>
              
              <div className="info-item">
                <label className="info-label">SKU Code *</label>
                <input
                  type="text"
                  name="sku_code"
                  className="form-control"
                  value={productData.sku_code}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter SKU code"
                />
              </div>

              <div className="info-item">
                <label className="info-label">HSN Code *</label>
                <input
                  type="text"
                  name="hsn_code"
                  className="form-control"
                  value={productData.hsn_code}
                  onChange={handleInputChange}
                  placeholder="Enter HSN code"
                  inputMode="numeric"
                  required
                />
              </div>
              
              {productData.category_id === 1 && (
                <div className="info-item">
                  <label className="info-label">Wattage (W) *</label>
                  <input
                    type="number"
                    name="wattage"
                    className="form-control"
                    value={productData.wattage}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter wattage"
                    min="1"
                  />
                </div>
              )}
              
              {/* <div className="info-item">
                <label className="info-label">Price (₹) *</label>
                <input
                  type="number"
                  name="price"
                  className="form-control"
                  value={productData.price}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter price"
                  min="0"
                  step="0.01"
                />
              </div> */}
              
              <div className="info-item">
                <label className="info-label">Warranty</label>
                <input
                  type="text"
                  name="warranty"
                  className="form-control"
                  value={productData.warranty}
                  onChange={handleInputChange}
                  placeholder="e.g., 25 years"
                />
              </div>

              <div className="info-item">
                <label className="info-label">Short Name</label>
                <input
                  type="text"
                  name="short_name"
                  className="form-control"
                  value={productData.short_name}
                  onChange={handleInputChange}
                  placeholder="e.g., SP-500W"
                />
              </div>

              <div className="info-item">
                <label className="info-label">Tags</label>
                <input
                  type="text"
                  name="tags"
                  className="form-control"
                  value={productData.tags}
                  onChange={handleInputChange}
                  placeholder="Comma separated tags (e.g., solar, panel, 500w)"
                />
              </div>

              <div className="info-item">
                <label className="info-label">DCR</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="dcr_applicable"
                    checked={productData.dcr_applicable}
                    onChange={handleInputChange}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span style={{ color: '#6c757d' }}></span>
                </div>
              </div>
            </div>
            
            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
              <label className="info-label">Description</label>
              <textarea
                name="description"
                className="form-control"
                value={productData.description}
                onChange={handleInputChange}
                rows="4"
                placeholder="Enter product description"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Images */}
          <div className="card">
            <h3 className="section-title">Product Images</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Thumbnail Image</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'thumbnail_image')}
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  Max size: 5MB, Format: JPEG, PNG
                </small>
              </div>
              
              <div className="info-item">
                <label className="info-label">Original Image</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'original_image')}
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  Max size: 5MB, Format: JPEG, PNG
                </small>
              </div>
              
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label className="info-label">Additional Images</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  multiple
                  onChange={handleMultipleImagesChange}
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  Select multiple images. Max size: 5MB each, Format: JPEG, PNG
                </small>
              </div>
            </div>
          </div>

          {/* Data Sheet */}
          <div className="card">
            <h3 className="section-title">Data Sheet</h3>
            <div className="info-item">
              <input
                type="file"
                className="form-control"
                accept=".pdf"
                onChange={(e) => handleFileChange(e, 'data_sheet')}
              />
              <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                Upload PDF data sheet for this product. Max size: 5MB, Format: PDF
              </small>
              {productData.data_sheet instanceof File && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#28a745' }}>
                  Selected: {productData.data_sheet.name}
                </div>
              )}
            </div>
          </div>

          {/* Discount Price Slabs */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
              <h3 className="section-title" style={{ marginBottom: '0' }}>Discount Price Slabs</h3>
              <button
                type="button"
                onClick={addDiscountPriceSlab}
                className="btn btn-primary"
                style={{ fontSize: '12px', padding: '8px 16px' }}
              >
                + Add Slab
              </button>
            </div>
            
            {productData.discount_price.map((slab, index) => (
              <div key={index} className="order-info" style={{ marginBottom: '15px', padding: '15px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
                <div className="info-item">
                  <label className="info-label">Quantity From</label>
                  <input
                    type="number"
                    className="form-control"
                    value={slab.qty}
                    onChange={(e) => handleDiscountPriceChange(index, 'qty', e.target.value)}
                    placeholder="From qty"
                    min="1"
                  />
                </div>
                
                <div className="info-item">
                  <label className="info-label">Quantity To (Max)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={slab.max}
                    onChange={(e) => handleDiscountPriceChange(index, 'max', e.target.value)}
                    placeholder="Max qty (leave empty for unlimited)"
                    min="1"
                  />
                </div>
                
                <div className="info-item">
                  <label className="info-label">Price (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={slab.price}
                    onChange={(e) => handleDiscountPriceChange(index, 'price', e.target.value)}
                    placeholder="Price for this slab"
                    min="0"
                    step="0.01"
                  />
                </div>
                
                <div className="info-item" style={{ display: 'flex', alignItems: 'end' }}>
                  {productData.discount_price.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDiscountPriceSlab(index)}
                      className="btn btn-secondary"
                      style={{ fontSize: '12px', padding: '8px 12px', backgroundColor: '#dc3545', borderColor: '#dc3545', color: 'white' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Specifications */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
              <h3 className="section-title" style={{ marginBottom: '0' }}>Specifications</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={toggleBulkSpecMode}
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '8px 16px' }}
                >
                  {bulkSpecMode ? '📝 Individual Edit' : '📋 Bulk Edit'}
                </button>
                {!bulkSpecMode && (
                  <button
                    type="button"
                    onClick={addSpecification}
                    className="btn btn-primary"
                    style={{ fontSize: '12px', padding: '8px 16px' }}
                  >
                    + Add Specification
                  </button>
                )}
              </div>
            </div>
            
            {bulkSpecMode ? (
              <div>
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '12px', 
                  backgroundColor: '#e7f3ff', 
                  border: '1px solid #b3d9ff', 
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#0056b3'
                }}>
                  <strong>💡 Bulk Edit Instructions:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Enter each specification on a new line</li>
                    <li>Use format: <code style={{ backgroundColor: '#fff', padding: '2px 4px', borderRadius: '3px' }}>Key:Value</code></li>
                    <li>Example: <code style={{ backgroundColor: '#fff', padding: '2px 4px', borderRadius: '3px' }}>Power Output:540W</code></li>
                    <li>Empty lines will be ignored</li>
                  </ul>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Specifications (Bulk Edit)</label>
                  <textarea
                    className="form-control"
                    value={bulkSpecText}
                    onChange={handleBulkSpecTextChange}
                    rows="12"
                    placeholder="Enter specifications in format:&#10;Power Output:540W&#10;Efficiency:21.2%&#10;Dimensions:2108x1048x35mm&#10;Weight:27.5kg&#10;Warranty:25 Years&#10;Cell Type:Monocrystalline"
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '14px',
                      resize: 'vertical',
                      lineHeight: '1.5'
                    }}
                  />
                </div>
                
                <div style={{ 
                  marginTop: '10px', 
                  padding: '10px', 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #e9ecef', 
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#6c757d'
                }}>
                  <strong>Preview:</strong> {bulkSpecText.split('\n').filter(line => line.trim() && line.includes(':')).length} specifications will be created
                </div>
              </div>
            ) : (
              productData.specifications.map((spec, index) => (
                <div key={index} className="order-info" style={{ marginBottom: '15px', padding: '15px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
                  <div className="info-item">
                    <label className="info-label">Specification Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={spec.key}
                      onChange={(e) => handleSpecificationChange(index, 'key', e.target.value)}
                      placeholder="e.g., Power Output"
                    />
                  </div>
                  
                  <div className="info-item">
                    <label className="info-label">Value</label>
                    <input
                      type="text"
                      className="form-control"
                      value={spec.value}
                      onChange={(e) => handleSpecificationChange(index, 'value', e.target.value)}
                      placeholder="e.g., 540W"
                    />
                  </div>
                  
                  <div className="info-item" style={{ display: 'flex', alignItems: 'end' }}>
                    {productData.specifications.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSpecification(index)}
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '8px 12px', backgroundColor: '#dc3545', borderColor: '#dc3545', color: 'white' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          {isMobile && (
            <div className="m-sticky-actions">
              <Link to="/products" className="btn btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-success"
              >
                {loading ? 'Creating...' : 'Create Product'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateProduct;